import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  Plus, Receipt, FileCheck, Search, Repeat, Package, ChevronRight,
  Clock, Trash2, CalendarClock, Pause, Play, Eye, Settings2, CreditCard
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { useTheme } from "../../contexts/ThemeContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import SearchableCustomerSelect from "../../components/shared/SearchableCustomerSelect";
import { ItemsLibrary, ItemAutocomplete, QuickSaveItemBtn } from "../../components/invoices/ItemsLibrary";
import InvoiceMonthlyCards from "../../components/invoices/InvoiceMonthlyCards";
import InvoiceDocList from "../../components/invoices/InvoiceDocList";
import InvoiceFormDialog from "../../components/invoices/InvoiceFormDialog";
import InvoiceViewDialog from "../../components/invoices/InvoiceViewDialog";
import InvoicePreviewPanel from "../../components/invoices/InvoicePreviewPanel";
import InvoiceSettingsTab from "../../components/invoices/InvoiceSettingsTab";
import SendSmsModal from "../../components/invoices/SendSmsModal";
import { SendForSignatureModal } from "../../components/tech/SendForSignatureModal";
import RecordPaymentDialog from "../../components/invoices/RecordPaymentDialog";
import TapToPayDialog from "../../components/payments/TapToPayDialog";
import { useTapToPayStatus } from "../../hooks/useTapToPay";
import SendReceiptDialog from "../../components/invoices/SendReceiptDialog";
import ChargeStripeDialog from "../../components/invoices/ChargeStripeDialog";
import PaymentsFeedTab from "../../components/invoices/PaymentsFeedTab";

const InvoicesPage = () => {
  const { isDark } = useTheme();
  const { timezone, getToday, formatDate: tzFormatDate } = useTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("type") === "estimate" ? "estimates" : "invoices");
  // Dashboard deep-link status focus (?status=open|overdue|partially_paid|paid|draft)
  const [statusFocus, setStatusFocus] = useState(() => searchParams.get("status") || "");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [createRecurringOpen, setCreateRecurringOpen] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [jobPrefill, setJobPrefill] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewFromView, setPreviewFromView] = useState(false);
  const [smsDoc, setSmsDoc] = useState(null);
  const [sigDoc, setSigDoc] = useState(null);
  const [smsPayLink, setSmsPayLink] = useState(null);
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [payDoc, setPayDoc] = useState(null);
  const [receiptInfo, setReceiptInfo] = useState(null);
  const [chargeDoc, setChargeDoc] = useState(null);
  const [ttpDoc, setTtpDoc] = useState(null);
  const [ttpAmount, setTtpAmount] = useState(null);
  const ttp = useTapToPayStatus("owner");
  const navigate = useNavigate();

  // Stable across renders so callbacks that depend on this don't churn.
  // Reads the token fresh per invocation so token rotation is picked up
  // automatically by the silent-refresh interceptor.
  const getHeaders = React.useCallback(() => {
    const tok = localStorage.getItem("token");
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  }, []);
  const token = localStorage.getItem("token");
  const headers = React.useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  useEffect(() => { fetchAll(); }, []);

  // Recover online payments that were never recorded (e.g. customer paid a
  // Stripe link but no webhook/return visit confirmed it). Silent when clean.
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/stripe/connect/reconcile-payments`, {}, { headers });
        const recovered = res.data?.recorded || [];
        if (recovered.length > 0) {
          toast.success(`${recovered.length} online payment${recovered.length > 1 ? "s" : ""} recovered and added to payment history.`);
          fetchAll();
        }
      } catch { /* silent — reconciliation is best-effort */ }
    })();
  }, []);

  // Handle Stripe Connect payment return
  useEffect(() => {
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (payment === "connect_paid" && sessionId) {
      (async () => {
        try {
          const res = await axios.get(`${API}/stripe/connect/payment-status/${sessionId}`, { headers });
          if (res.data.payment_status === "paid") {
            if (res.data.invoice_status === "partially_paid") {
              toast.success("Deposit received! Invoice marked Partially Paid.");
            } else {
              toast.success("Payment received! Invoice marked as paid.");
            }
            fetchAll();
          } else {
            toast.info("Payment is being processed.");
          }
        } catch { toast.error("Could not verify payment status."); }
        finally { setSearchParams({}); }
      })();
    } else if (payment === "cancelled") {
      toast.info("Payment was cancelled.");
      setSearchParams({});
    }
  }, []);

  // Handle job prefill from URL
  useEffect(() => {
    const prefillJobId = searchParams.get("prefill_job");
    const prefillType = searchParams.get("type");
    if (prefillJobId && token) {
      (async () => {
        try {
          const res = await axios.get(`${API}/jobs/${prefillJobId}`, { headers });
          const job = res.data;
          const items = [];
          if (job.service_name || job.title) items.push({ description: job.service_name || job.title, quantity: 1, unit_price: job.total_charged || job.price || 0, discount: 0 });
          if (job.parts?.length > 0) for (const part of job.parts) items.push({ description: `Part: ${part.name}`, quantity: 1, unit_price: part.cost || 0, discount: 0 });
          if (items.length === 0) items.push({ description: "", quantity: 1, unit_price: 0, discount: 0 });
          setJobPrefill({ customer_id: job.customer_id || "", job_id: job.job_id, items, docType: prefillType === "estimate" ? "estimate" : "invoice" });
          setActiveTab(prefillType === "estimate" ? "estimates" : "invoices");
          setCreateOpen(true);
          setSearchParams({});
        } catch { toast.error("Failed to load job data"); }
      })();
    }
  }, [searchParams]);

  // Handle ?open=<invoice_id> deep link (e.g. from a job's linked documents)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && token) {
      (async () => {
        try {
          const res = await axios.get(`${API}/invoices/${openId}`, { headers: getHeaders() });
          const d = res.data;
          setActiveTab((d.document_type || "invoice") === "estimate" ? "estimates" : "invoices");
          setSelected(d);
          setViewOpen(true);
        } catch { toast.error("Document not found"); }
        finally { setSearchParams({}); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Replace a doc everywhere it's held in state (list row, open view
  // dialog, preview panel) so edits show instantly — no page refresh.
  const applyDocUpdate = React.useCallback((doc) => {
    if (!doc?.invoice_id) return;
    setInvoices(prev => {
      const idx = prev.findIndex(i => i.invoice_id === doc.invoice_id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...doc };
      return next;
    });
    setSelected(prev => (prev && prev.invoice_id === doc.invoice_id ? { ...prev, ...doc } : prev));
    setPreviewDoc(prev => (prev && prev.invoice_id === doc.invoice_id ? { ...prev, ...doc } : prev));
  }, []);

  // Re-fetch a single doc so the open view dialog reflects payment changes.
  const refreshSelected = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}`, { headers: getHeaders() });
      applyDocUpdate(res.data);
      return res.data;
    } catch { return null; }
  };

  const handleRecordPayment = async (payload) => {
    if (!payDoc) return;
    try {
      const res = await axios.post(`${API}/invoices/${payDoc.invoice_id}/payments`, payload, { headers: getHeaders() });
      toast.success(res.data?.fully_paid ? "Payment recorded — invoice fully paid!" : "Deposit recorded");
      if (res.data?.job_status_updated) toast.info("Job marked as Deposit Collected");
      await refreshSelected(payDoc.invoice_id);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to record payment");
      throw e;
    }
  };

  const handleDeletePayment = async (doc, paymentId) => {
    if (!window.confirm("Delete this payment record? The invoice balance and status will be recalculated.")) return;
    try {
      await axios.delete(`${API}/invoices/${doc.invoice_id}/payments/${paymentId}`, { headers: getHeaders() });
      toast.success("Payment deleted");
      await refreshSelected(doc.invoice_id);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to delete payment");
    }
  };

  const handleSendReceipt = async (via) => {
    if (!receiptInfo) return;
    const { doc, payment } = receiptInfo;
    try {
      const res = await axios.post(
        `${API}/invoices/${doc.invoice_id}/payments/${payment.payment_id}/receipt`,
        { via },
        { headers: getHeaders() }
      );
      toast.success(res.data?.message || "Receipt sent");
      setReceiptInfo(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to send receipt");
    }
  };

  const INVOICES_PAGE_SIZE = 50;
  const fetchSeqRef = React.useRef(0);
  const debounceRef = React.useRef(null);
  const nextCursorRef = React.useRef("");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listTotal, setListTotal] = useState(0);

  // Multi-location filter — "all" = company-wide, "unassigned" = no
  // location stamped, or a specific location_id.
  const [locationFilter, setLocationFilter] = useState("all");
  const [storeLocations, setStoreLocations] = useState([]);
  const showLocationFilter = storeLocations.length >= 2;
  React.useEffect(() => {
    axios.get(`${API}/store/locations`, { headers: getHeaders() })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.locations || []);
        setStoreLocations(list);
      })
      .catch(() => setStoreLocations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the GET /api/invoices query for the current tab + search.
  const buildInvoicesQuery = React.useCallback((cursor = "") => {
    const p = new URLSearchParams();
    p.set("limit", String(INVOICES_PAGE_SIZE));
    if (cursor) p.set("cursor", cursor);
    p.set("document_type", activeTab === "estimates" ? "estimate" : "invoice");
    if (search && search.trim().length >= 2) p.set("q", search.trim());
    if (locationFilter && locationFilter !== "all") p.set("location_id", locationFilter);
    if (statusFocus) p.set("status", statusFocus);
    return p;
  }, [activeTab, search, locationFilter, statusFocus]);

  // Fetch invoices/estimates with server-side filter+pagination. Append=true
  // adds the next page (infinite scroll); else replaces the dataset.
  const fetchInvoicesPage = React.useCallback(async ({ cursor = "", append = false } = {}) => {
    const seq = ++fetchSeqRef.current;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const params = buildInvoicesQuery(cursor);
      const res = await axios.get(`${API}/invoices?${params}`, { headers: getHeaders() });
      if (seq !== fetchSeqRef.current) return;
      const data = Array.isArray(res.data) ? res.data : [];
      setInvoices(prev => append ? [...prev, ...data] : data);
      nextCursorRef.current = res.headers?.["x-next-cursor"] || "";
      setHasMore(Boolean(nextCursorRef.current));
      const tc = parseInt(res.headers?.["x-total-count"] || "0", 10);
      if (Number.isFinite(tc)) setListTotal(tc);
    } catch {
      if (!append) toast.error("Failed to load invoices");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [buildInvoicesQuery, getHeaders]);

  // Reference data (customers, recurring, paymentTypes, stripe-connect, biz settings)
  // is small + cached; load it once on mount alongside the first invoices page.
  const fetchAll = async () => {
    try {
      const [custRes, statsRes, recRes, ptRes, connectRes, bizRes] = await Promise.all([
        axios.get(`${API}/customers`, { headers }),
        axios.get(`${API}/invoices/stats`, { headers }),
        axios.get(`${API}/invoices/recurring`, { headers }),
        axios.get(`${API}/settings/payment-types`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/stripe/connect/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/settings/business`, { headers }).catch(() => ({ data: {} })),
      ]);
      setCustomers(custRes.data || []);
      setStats(statsRes.data || {});
      setRecurring(recRes.data || []);
      setPaymentTypes(Array.isArray(ptRes.data) ? ptRes.data : []);
      setConnectStatus(connectRes.data || null);
      const biz = bizRes.data || {};
      setBusinessName(biz.business_name || biz.company_name || "");
    } catch { toast.error("Failed to load data"); }
    // Also refetch the paged invoices list so post-mutation callsites
    // (status change, delete, convert) see the updated row.
    nextCursorRef.current = "";
    fetchInvoicesPage({ cursor: "", append: false });
  };

  // Refetch the paged list whenever the active tab / search / debounce fires.
  useEffect(() => {
    if (!token) return;
    if (activeTab === "recurring" || activeTab === "items" || activeTab === "settings" || activeTab === "payments") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = search && search.trim().length >= 2 ? 300 : 0;
    debounceRef.current = setTimeout(() => {
      nextCursorRef.current = "";
      fetchInvoicesPage({ cursor: "", append: false });
    }, delay);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fetchInvoicesPage, token, activeTab, search]);

  // Infinite-scroll handler — passed down to InvoiceDocList.
  const loadMoreInvoices = React.useCallback(() => {
    if (loadingMore || !hasMore || !nextCursorRef.current) return;
    fetchInvoicesPage({ cursor: nextCursorRef.current, append: true });
  }, [loadingMore, hasMore, fetchInvoicesPage]);

  // Fetch monthly stats when tab changes
  useEffect(() => {
    if (!token) return;
    const docType = activeTab === "estimates" ? "estimate" : "invoice";
    if (activeTab === "recurring" || activeTab === "items" || activeTab === "settings" || activeTab === "payments") return;
    axios.get(`${API}/invoices/monthly-stats?document_type=${docType}`, { headers })
      .then(res => setMonthlyStats(res.data || []))
      .catch(() => setMonthlyStats([]));
  }, [activeTab, invoices]);

  // Server has already filtered by document_type + q + status, so the
  // client-side `filtered` is now mostly a passthrough that preserves a
  // deterministic newest-first sort (paged response is already sorted but
  // mutations / appends could intermix dates).
  const filtered = useMemo(() => {
    return [...invoices].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [invoices]);

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/invoices/${id}/status`, { status }, { headers });
      toast.success("Status updated");
      // Refresh the open view dialog in place — no close/reopen needed.
      await refreshSelected(id);
      fetchAll();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/invoices/${id}`, { headers });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleConvert = async (id) => {
    try {
      await axios.post(`${API}/invoices/${id}/convert`, {}, { headers });
      toast.success("Estimate converted to invoice!");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert"); }
  };

  const handleSendEmail = async (invoice, payLink) => {
    const customer = customers.find(c => c.customer_id === invoice.customer_id);
    const email = customer?.email || invoice.customer_email;
    if (!email) { toast.error("Customer has no email on file"); return; }
    try {
      await axios.post(`${API}/email/send-invoice`, {
        invoice_id: invoice.invoice_id, customer_email: email,
        customer_name: customer?.name || invoice.customer_name || "Customer",
        payment_link: payLink?.url || undefined,
        payment_amount: payLink?.amount || undefined,
      }, { headers });
      toast.success(payLink?.url ? `Payment link emailed to ${email}` : `Email sent to ${email}`);
      fetchAll();
    } catch (err) {
      // Surface backend-supplied detail (e.g. the strict-sender missing message
      // "No sender email is configured for <Location>. Add and verify a
      // location sender in Settings → Email before sending.") verbatim so the
      // user knows exactly what to fix. Include a deep-link CTA to the tab.
      const detail = err.response?.data?.detail || "Failed to send email";
      const isSenderIssue = /Settings\s*[→>]/i.test(detail) || /verified/i.test(detail);
      if (isSenderIssue) {
        toast.error(detail, {
          duration: 8000,
          action: { label: "Open Email Settings", onClick: () => { window.location.href = "/dashboard/settings?tab=email"; } },
        });
      } else {
        toast.error(detail);
      }
    }
  };

  // Opens the channel-picker modal (Email / SMS) instead of sending directly.
  const handleSendForSignature = (invoice) => {
    // Close the Radix view dialog first — while open it keeps
    // pointer-events:none on <body>, freezing this custom modal.
    setViewOpen(false);
    setSigDoc(invoice);
  };

  const handleSignatureSent = (invoice, data) => {
    // Optimistically update the open dialog so the user immediately sees
    // the "Awaiting Signature" badge + banner without closing & re-opening.
    setSelected(prev =>
      prev && prev.invoice_id === invoice.invoice_id
        ? {
            ...prev,
            signature_status: "sent_for_signature",
            signature_sent_at: new Date().toISOString(),
            public_token: data?.public_token || prev.public_token,
            status: prev.status === "draft" ? "sent" : prev.status,
          }
        : prev
    );
    fetchAll();
  };

  const handleSendSms = async ({ phone_number }) => {
    if (!smsDoc) return;
    setSmsSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/invoices/${smsDoc.invoice_id}/send-sms`,
        {
          phone_number: phone_number || undefined,
          payment_link: smsPayLink?.url || undefined,
          payment_amount: smsPayLink?.amount || undefined,
        },
        { headers }
      );
      toast.success(res.data?.message || "SMS sent");
      setSmsDoc(null);
      setSmsPayLink(null);
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Failed to send SMS");
      toast.error(msg);
    } finally {
      setSmsSubmitting(false);
    }
  };

  const downloadPdf = async (invoice) => {
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/invoices/${invoice.invoice_id}/pdf`;
      const filename = `${invoice.invoice_number}.pdf`;
      const { downloadOrSharePdf } = await import("../../utils/pdfShare");
      const result = await downloadOrSharePdf(url, filename, token);
      if (result.method === "webshare_cancelled") return; // user cancelled, no toast
      if (result.method === "native") toast.success("Preparing PDF for sharing...");
      else if (result.method === "webshare") toast.success("PDF shared");
      else toast.success("PDF downloaded");
    } catch { toast.error("Failed to download PDF"); }
  };

  // Opens the amount chooser (deposit / balance / custom) — the dialog
  // creates the Stripe checkout link itself.
  const handleChargeViaStripe = (invoice) => setChargeDoc(invoice);

  const toggleRecurring = async (id) => {
    try {
      const res = await axios.patch(`${API}/invoices/recurring/${id}/toggle`, {}, { headers });
      setRecurring(prev => prev.map(r => r.recurring_id === id ? { ...r, is_active: res.data.is_active } : r));
      toast.success(res.data.is_active ? "Resumed" : "Paused");
    } catch { toast.error("Failed to toggle"); }
  };

  const deleteRecurring = async (id) => {
    if (!window.confirm("Delete this recurring invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/recurring/${id}`, { headers });
      setRecurring(prev => prev.filter(r => r.recurring_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  // Called by InvoiceFormDialog with the saved doc from the API response.
  // Instantly overwrites stale state, then refreshes stats in the background.
  const handleDocSaved = (savedDoc) => {
    if (savedDoc?.invoice_id) {
      const matchesTab = (savedDoc.document_type || "invoice") === (activeTab === "estimates" ? "estimate" : "invoice");
      setInvoices(prev => {
        const idx = prev.findIndex(i => i.invoice_id === savedDoc.invoice_id);
        if (idx === -1) return matchesTab ? [savedDoc, ...prev] : prev;
        const next = [...prev];
        next[idx] = { ...next[idx], ...savedDoc };
        return next;
      });
      setSelected(prev => (prev && prev.invoice_id === savedDoc.invoice_id ? { ...prev, ...savedDoc } : prev));
      setPreviewDoc(prev => (prev && prev.invoice_id === savedDoc.invoice_id ? { ...prev, ...savedDoc } : prev));
    }
    fetchAll();
  };

  const handleEdit = (doc) => {
    setViewOpen(false);
    setEditingDoc(doc);
    setCreateOpen(true);
  };

  const isEstimateTab = activeTab === "estimates";
  const isRecurringTab = activeTab === "recurring";
  const isItemsTab = activeTab === "items";
  const isSettingsTab = activeTab === "settings";
  const isPaymentsTab = activeTab === "payments";

  // Open an invoice from the payments feed (it may not be in the loaded list)
  const openInvoiceById = async (invoiceId) => {
    const local = invoices.find(i => i.invoice_id === invoiceId);
    if (local) { setSelected(local); setViewOpen(true); return; }
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}`, { headers });
      setSelected(res.data);
      setViewOpen(true);
    } catch { toast.error("Could not load invoice"); }
  };

  return (
    <div className="space-y-5" data-testid="invoices-page">
      {/* Page Title */}
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`} data-testid="page-title">
          {isSettingsTab ? "Invoice Settings" : isItemsTab ? "Items Library" : isRecurringTab ? "Recurring" : isPaymentsTab ? "Payments" : isEstimateTab ? "Estimates" : "Invoices"}
        </h1>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <TabsList className={`p-1 ${isDark ? "bg-zinc-800/60 border border-zinc-700/50" : "bg-gray-100 border border-gray-200"}`}>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-invoices">
              <Receipt className="w-3.5 h-3.5 hidden sm:block" /> Invoices
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-current ml-0.5 hidden sm:flex">{stats.total_invoices || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="estimates" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-estimates">
              <FileCheck className="w-3.5 h-3.5 hidden sm:block" /> Estimates
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-current ml-0.5 hidden sm:flex">{stats.total_estimates || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="recurring" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-recurring">
              <Repeat className="w-3.5 h-3.5 hidden sm:block" /> Recurring
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-current ml-0.5 hidden sm:flex">{recurring.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-payments">
              <CreditCard className="w-3.5 h-3.5 hidden sm:block" /> Payments
            </TabsTrigger>
            <TabsTrigger value="items" className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-items">
              <Package className="w-3.5 h-3.5 hidden sm:block" /> Items
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-settings">
              <Settings2 className="w-3.5 h-3.5 hidden sm:block" /> Settings
            </TabsTrigger>
          </TabsList>
          {!isItemsTab && !isRecurringTab && !isSettingsTab && !isPaymentsTab && (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {showLocationFilter && (
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="h-9 text-sm w-full sm:w-44" data-testid="invoices-location-filter">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {storeLocations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className={`relative w-full sm:w-56`}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-9 text-sm" data-testid="search-input" />
              </div>
            </div>
          )}
        </div>
      </Tabs>

      {/* Monthly Summary Cards */}
      {!isRecurringTab && !isItemsTab && !isSettingsTab && !isPaymentsTab && (
        <InvoiceMonthlyCards months={monthlyStats} isEstimate={isEstimateTab} isDark={isDark} />
      )}

      {/* Collections snapshot — outstanding / overdue / deposits / collected */}
      {!isEstimateTab && !isRecurringTab && !isItemsTab && !isSettingsTab && !isPaymentsTab && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="invoice-stats-strip">
          {[
            { label: "Outstanding", value: stats.total_outstanding, cls: "text-blue-500", testid: "stat-outstanding" },
            { label: "Overdue", value: stats.total_overdue, cls: "text-red-500", testid: "stat-overdue" },
            { label: "Deposits Held", value: stats.deposits_collected, cls: "text-amber-500", sub: `${stats.partially_paid_count || 0} partially paid`, testid: "stat-deposits" },
            { label: "Collected This Month", value: stats.paid_this_month, cls: "text-emerald-500", testid: "stat-collected" },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl px-3 py-2.5 ${isDark ? "bg-zinc-900/50 border border-zinc-800" : "bg-white border border-gray-200 shadow-sm"}`} data-testid={s.testid}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className={`text-base font-bold ${s.cls}`}>${(s.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              {s.sub && <p className="text-[10px] text-muted-foreground">{s.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Create CTA + Customize Row */}
      {!isRecurringTab && !isItemsTab && !isSettingsTab && !isPaymentsTab && (
        <div className="flex gap-2">
          <button
            onClick={() => { setEditingDoc(null); setCreateOpen(true); }}
            className={`flex-1 rounded-xl py-3.5 px-5 flex items-center justify-between text-white font-semibold text-sm transition-all hover:shadow-lg hover:scale-[1.005] active:scale-[0.995] ${
              isEstimateTab
                ? "bg-gradient-to-r from-orange-500 to-amber-400"
                : "bg-gradient-to-r from-blue-600 to-blue-400"
            }`}
            data-testid="create-btn"
          >
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              <span className="tracking-wide">{isEstimateTab ? "CREATE NEW ESTIMATE" : "CREATE NEW INVOICE"}</span>
            </div>
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => { setPreviewDoc(null); setPreviewOpen(true); }}
            className={`rounded-xl px-4 flex items-center justify-center transition-all hover:shadow-md ${
              isDark ? "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-sm"
            }`}
            title="Customize Template"
            data-testid="customize-btn"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      )}

      {statusFocus && !isRecurringTab && !isItemsTab && !isSettingsTab && !isPaymentsTab && (
        <div className="flex items-center gap-2 mb-3" data-testid="invoice-focus-banner">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/40">
            Showing: {({ open: "Unpaid / partially paid", overdue: "Overdue", partially_paid: "Partially paid", paid: "Paid", draft: "Drafts" })[statusFocus] || statusFocus}
            <button onClick={() => setStatusFocus("")} className="hover:text-foreground" data-testid="invoice-focus-clear">✕</button>
          </span>
        </div>
      )}
      {/* Invoice/Estimate List */}
      {!isRecurringTab && !isItemsTab && !isSettingsTab && !isPaymentsTab && (
        loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : (
          <InvoiceDocList
            documents={filtered}
            isEstimate={isEstimateTab}
            isDark={isDark}
            onView={(doc) => { setSelected(doc); setViewOpen(true); }}
            onEdit={handleEdit}
            onEmail={handleSendEmail}
            onSendSms={(doc) => setSmsDoc(doc)}
            onDownload={downloadPdf}
            onDelete={handleDelete}
            onConvert={handleConvert}
            onCharge={handleChargeViaStripe}
            connectStatus={connectStatus}
            onEndReached={loadMoreInvoices}
            loadingMore={loadingMore}
            hasMore={hasMore}
          />
        )
      )}

      {/* Recurring Tab */}
      {isRecurringTab && (
        <RecurringSection
          recurring={recurring}
          isDark={isDark}
          onToggle={toggleRecurring}
          onDelete={deleteRecurring}
          onCreate={() => setCreateRecurringOpen(true)}
        />
      )}

      {/* Payments Feed Tab */}
      {isPaymentsTab && (
        <PaymentsFeedTab isDark={isDark} headers={headers} onOpenInvoice={openInvoiceById} />
      )}

      {/* Items Library Tab */}
      {isItemsTab && (
        <Card className={isDark ? "bg-zinc-900/50 border-zinc-800" : "bg-white border-gray-200 shadow-sm"} data-testid="items-library">
          <CardContent className="p-4 sm:p-6">
            <ItemsLibrary isDark={isDark} headers={headers} />
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {isSettingsTab && (
        <InvoiceSettingsTab isDark={isDark} headers={headers} />
      )}

      {/* View Dialog */}
      <InvoiceViewDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        doc={selected}
        isDark={isDark}
        onSendEmail={handleSendEmail}
        onSendSms={(doc) => { setViewOpen(false); setSmsDoc(doc); }}
        onDownloadPdf={downloadPdf}
        onConvert={handleConvert}
        connectStatus={connectStatus}
        onChargeViaStripe={handleChargeViaStripe}
        onEdit={handleEdit}
        onStatusChange={handleStatusChange}
        onPreview={(doc) => { setPreviewDoc(doc); setPreviewFromView(true); setPreviewOpen(true); }}
        onSendForSignature={handleSendForSignature}
        onRecordPayment={(doc) => setPayDoc(doc)}
        onDeletePayment={handleDeletePayment}
        onSendReceipt={(doc, payment) => setReceiptInfo({ doc, payment })}
      />

      {/* Record Payment (deposit / partial) Dialog */}
      <RecordPaymentDialog
        open={!!payDoc}
        onClose={() => setPayDoc(null)}
        doc={payDoc ? (selected?.invoice_id === payDoc.invoice_id ? selected : payDoc) : null}
        isDark={isDark}
        onSubmit={handleRecordPayment}
        onTapToPay={ttp.native ? (doc, amount) => {
          setPayDoc(null);
          if (ttp.eligible) { setTtpAmount(amount); setTtpDoc(doc); }
          else {
            // Apple req 5.3: the button always invokes the Tap to Pay path —
            // owners/admins are routed to the enablement screen.
            toast.info("Set up Tap to Pay in Settings to accept contactless payments");
            navigate("/dashboard/settings?tab=stripe-connect");
          }
        } : undefined}
      />

      {/* Tap to Pay (NFC, native mobile app only) */}
      <TapToPayDialog
        open={!!ttpDoc}
        onClose={() => setTtpDoc(null)}
        doc={ttpDoc ? (selected?.invoice_id === ttpDoc.invoice_id ? selected : ttpDoc) : null}
        initialAmount={ttpAmount}
        api={{
          createIntent: async (amount, reqId) =>
            (await axios.post(`${API}/terminal/payment-intents`,
              { invoice_id: ttpDoc.invoice_id, amount, client_request_id: reqId },
              { headers: getHeaders() })).data,
          finalize: async (piId) =>
            (await axios.post(`${API}/terminal/payment-intents/${piId}/finalize`, {}, { headers: getHeaders() })).data,
          cancel: async (piId) =>
            (await axios.post(`${API}/terminal/payment-intents/${piId}/cancel`, {}, { headers: getHeaders() })).data,
          tokenEndpoint: `${API}/terminal/connection-token`,
          authToken: () => localStorage.getItem("token"),
          tosAllowed: true,
          sendReceipt: async (paymentId, via) =>
            axios.post(`${API}/invoices/${ttpDoc.invoice_id}/payments/${paymentId}/receipt`, { via }, { headers: getHeaders() }),
          sendDeclineReceipt: async (piId, via) =>
            axios.post(`${API}/terminal/payment-intents/${piId}/decline-receipt`, { via }, { headers: getHeaders() }),
        }}
        onPaid={async () => {
          await refreshSelected(ttpDoc.invoice_id);
          fetchAll();
        }}
      />

      {/* Send Payment Receipt Dialog */}
      <SendReceiptDialog
        open={!!receiptInfo}
        onClose={() => setReceiptInfo(null)}
        info={receiptInfo}
        isDark={isDark}
        onSend={handleSendReceipt}
      />

      {/* Charge via Stripe — amount chooser */}
      <ChargeStripeDialog
        open={!!chargeDoc}
        onClose={() => setChargeDoc(null)}
        doc={chargeDoc ? (selected?.invoice_id === chargeDoc.invoice_id ? selected : chargeDoc) : null}
        isDark={isDark}
        headers={headers}
        onTapToPay={ttp.native && ttp.status?.connect_ready ? (doc, amount) => {
          setViewOpen(false);
          if (ttp.eligible) { setTtpAmount(amount); setTtpDoc(doc); }
          else {
            // Apple req 5.3: the button always invokes the Tap to Pay path —
            // owners/admins are routed to the enablement screen.
            toast.info("Set up Tap to Pay in Settings to accept contactless payments");
            navigate("/dashboard/settings?tab=stripe-connect");
          }
        } : undefined}
        onSendSms={(doc, payLink) => {
          // Close the view dialog too — a still-open Radix dialog keeps
          // pointer-events:none on <body>, freezing the custom SMS modal.
          setViewOpen(false);
          setSmsPayLink(payLink || null);
          setSmsDoc(doc);
        }}
        onSendEmail={(doc, payLink) => {
          setViewOpen(false);
          handleSendEmail(doc, payLink);
        }}
      />

      {/* SMS Send Modal */}
      <SendSmsModal
        open={!!smsDoc}
        onClose={() => { setSmsDoc(null); setSmsPayLink(null); }}
        doc={smsDoc}
        customer={smsDoc ? customers.find(c => c.customer_id === smsDoc.customer_id) : null}
        businessName={businessName}
        paymentLink={smsPayLink}
        onSubmit={handleSendSms}
        submitting={smsSubmitting}
        isDark={isDark}
      />

      {/* Send for Signature Modal — channel picker (Email / SMS) */}
      {sigDoc && (
        <SendForSignatureModal
          doc={sigDoc}
          mode="owner"
          isDark={isDark}
          onClose={() => setSigDoc(null)}
          onSent={(data) => handleSignatureSent(sigDoc, data)}
        />
      )}

      {/* Create/Edit Dialog */}
      <InvoiceFormDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setJobPrefill(null); setEditingDoc(null); }}
        customers={customers}
        isEstimate={isEstimateTab}
        isDark={isDark}
        headers={headers}
        onCreated={handleDocSaved}
        paymentTypes={paymentTypes}
        jobPrefill={jobPrefill}
        defaultCompanyName={businessName}
        editingDoc={editingDoc}
        onPreview={(doc) => { setPreviewDoc(doc); setPreviewOpen(true); }}
      />

      {/* Create Recurring Dialog */}
      <CreateRecurringDialog
        open={createRecurringOpen}
        onClose={() => setCreateRecurringOpen(false)}
        customers={customers}
        isDark={isDark}
        headers={headers}
        onCreated={fetchAll}
      />

      {/* Preview & Customize Panel */}
      <InvoicePreviewPanel
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          // Reopen the invoice detail modal when preview was launched from it.
          if (previewFromView) { setPreviewFromView(false); setViewOpen(true); }
        }}
        isDark={isDark}
        headers={headers}
        invoice={previewDoc}
      />
    </div>
  );
};

/* ──── Recurring Section ──── */
const FREQ_LABELS = { weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

const RecurringSection = ({ recurring, isDark, onToggle, onDelete, onCreate }) => {
  if (recurring.length === 0) {
    return (
      <div className={`text-center py-16 rounded-xl ${isDark ? "bg-zinc-900/50 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}>
        <Repeat className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
        <p className="text-muted-foreground text-sm mb-4">No recurring invoices set up yet</p>
        <Button onClick={onCreate} className="bg-gradient-to-r from-purple-600 to-purple-400 text-white hover:shadow-lg" data-testid="empty-create-recurring">
          <Plus className="w-4 h-4 mr-1" /> Create Schedule
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Button onClick={onCreate} className="bg-gradient-to-r from-purple-600 to-purple-400 text-white hover:shadow-lg w-full sm:w-auto" data-testid="create-recurring-btn">
        <Plus className="w-4 h-4 mr-1.5" /> New Schedule
      </Button>
      <div className="grid gap-3 md:grid-cols-2" data-testid="recurring-list">
        {recurring.map((rec, i) => (
          <div
            key={rec.recurring_id}
            className={`group rounded-xl border p-5 transition-all ${
              rec.is_active
                ? isDark ? "border-purple-500/30 bg-purple-500/[0.03]" : "border-purple-200 bg-purple-50/30 shadow-sm"
                : isDark ? "border-zinc-800 bg-zinc-900/50 opacity-60" : "border-gray-200 bg-white opacity-60 shadow-sm"
            }`}
            data-testid={`recurring-card-${i}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${rec.is_active ? "bg-purple-500/15" : "bg-gray-500/15"}`}>
                    <Repeat className={`w-4 h-4 ${rec.is_active ? "text-purple-500" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{rec.customer_name}</h4>
                    <p className="text-xs text-muted-foreground">{FREQ_LABELS[rec.frequency] || rec.frequency} &middot; ${rec.total?.toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 text-muted-foreground border-border">
                    <CalendarClock className="w-2.5 h-2.5" /> Next: {rec.next_run ? format(new Date(rec.next_run), "MMM d, yyyy") : "N/A"}
                  </Badge>
                  {rec.end_date && (
                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 text-muted-foreground border-border">
                      <Clock className="w-2.5 h-2.5" /> Ends: {format(new Date(rec.end_date), "MMM d, yyyy")}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 text-muted-foreground border-border">
                    <Receipt className="w-2.5 h-2.5" /> {rec.invoices_generated} generated
                  </Badge>
                  {rec.auto_send && (
                    <Badge className="bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 text-[10px] h-5 px-1.5">Auto-send</Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Switch checked={rec.is_active} onCheckedChange={() => onToggle(rec.recurring_id)} className="data-[state=checked]:bg-purple-500" data-testid={`toggle-recurring-${i}`} />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => onDelete(rec.recurring_id)} data-testid={`delete-recurring-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ──── Create Recurring Dialog ──── */
const CreateRecurringDialog = ({ open, onClose, customers, isDark, headers, onCreated }) => {
  const { getToday } = useTimezone();
  const [form, setForm] = useState({
    customer_id: "", frequency: "monthly", start_date: "", end_date: "",
    auto_send: false, payment_terms: "Net 30", notes: "",
    items: [{ description: "", quantity: 1, unit_price: 0, discount: 0 }],
    tax_rate: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const tomorrow = getToday();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setForm({
        customer_id: "", frequency: "monthly", start_date: tomorrow.toISOString().split('T')[0], end_date: "",
        auto_send: false, payment_terms: "Net 30", notes: "",
        items: [{ description: "", quantity: 1, unit_price: 0, discount: 0 }],
        tax_rate: 0,
      });
    }
  }, [open]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0, discount: 0 }] }));
  const removeItem = (i) => { if (form.items.length > 1) setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); };
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm(f => ({ ...f, items }));
  };

  const subtotal = form.items.reduce((s, it) => s + it.quantity * it.unit_price, 0);
  const discountTotal = form.items.reduce((s, it) => s + (it.quantity * it.unit_price * (it.discount || 0) / 100), 0);
  const afterDiscount = subtotal - discountTotal;
  const taxAmount = afterDiscount * (form.tax_rate / 100);
  const total = afterDiscount + taxAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!form.items[0]?.description) { toast.error("Add at least one item"); return; }
    if (!form.start_date) { toast.error("Start date is required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/invoices/recurring`, {
        ...form, subtotal, discount_total: discountTotal, tax_amount: taxAmount, total,
        start_date: new Date(form.start_date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      }, { headers });
      toast.success("Recurring invoice created!");
      onClose(); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-recurring-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-purple-500" /> Create Recurring Invoice</DialogTitle>
          <DialogDescription>Set up an auto-generating invoice schedule</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Customer *</Label>
              <SearchableCustomerSelect customers={customers} value={form.customer_id} onChange={(v) => setForm(f => ({ ...f, customer_id: v }))} placeholder="Select customer" testId="rec-customer" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm(f => ({ ...f, frequency: v }))}>
                <SelectTrigger className="h-9" data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Start Date *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} className="h-9 text-sm" data-testid="rec-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">End Date (opt)</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} className="h-9 text-sm" data-testid="rec-end" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tax Rate (%)</Label>
              <Input type="number" step="0.1" min="0" max="100" value={form.tax_rate} onChange={(e) => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))} className="h-9 text-sm" data-testid="rec-tax" />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Line Items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addItem} className="text-purple-500 text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
            {form.items.map((item, i) => {
              const lineTotal = item.quantity * item.unit_price * (1 - (item.discount || 0) / 100);
              return (
                <div key={i} className={`p-2 rounded-lg ${isDark ? "bg-zinc-800/50" : "bg-gray-50"}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-4">
                      <ItemAutocomplete value={item.description} onChange={(val) => updateItem(i, "description", val)} onSelect={(saved) => { updateItem(i, "description", saved.name + (saved.description ? ` - ${saved.description}` : "")); updateItem(i, "unit_price", saved.unit_price || 0); updateItem(i, "quantity", saved.default_quantity || 1); }} isDark={isDark} headers={headers} />
                    </div>
                    <div className="col-span-2"><Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(i, "quantity", parseInt(e.target.value) || 1)} className="h-8 text-sm" /></div>
                    <div className="col-span-2"><Input type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="h-8 text-sm" /></div>
                    <div className="col-span-2"><Input type="number" step="1" min="0" max="100" value={item.discount} onChange={(e) => updateItem(i, "discount", parseFloat(e.target.value) || 0)} className="h-8 text-sm" /></div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <span className="text-sm font-medium text-foreground">${lineTotal.toFixed(2)}</span>
                      {form.items.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} className="h-6 w-6 text-red-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></Button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Totals */}
          <div className={`rounded-lg p-3 space-y-1.5 ${isDark ? "bg-zinc-800/30 border border-zinc-700" : "bg-gray-50 border border-gray-200"}`}>
            <div className="flex justify-between text-sm text-muted-foreground"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            {discountTotal > 0 && <div className="flex justify-between text-sm text-orange-500"><span>Discount</span><span>-${discountTotal.toFixed(2)}</span></div>}
            {form.tax_rate > 0 && <div className="flex justify-between text-sm text-muted-foreground"><span>Tax ({form.tax_rate}%)</span><span>${taxAmount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-lg font-bold text-foreground pt-1.5 border-t border-dashed" style={{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e5e7eb" }}><span>Total</span><span>${total.toFixed(2)}</span></div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="h-9 text-sm" /></div>
            <div className="flex items-center gap-2 pt-5">
              <Switch id="auto-send" checked={form.auto_send} onCheckedChange={(v) => setForm(f => ({ ...f, auto_send: v }))} data-testid="rec-auto-send" />
              <Label htmlFor="auto-send" className="text-sm cursor-pointer">Auto-send on creation</Label>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700" disabled={submitting} data-testid="rec-submit">
              {submitting ? "Creating..." : "Create Schedule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicesPage;
